/**
 * Clientes (épico 5; docs/design/pranchas/01-clientes.png): busca por nome ou telefone, Quem sumiu e
 * quem tem pacote. No celular a lista e a ficha são telas separadas; do tablet em diante ficam lado a lado.
 */
import { type ClientRow, clientLineText, clientRows, matchClient, missingMessage, todayIn } from '@lindeza/core';
import { useLiveQuery, useMissingClients, useProfessional, useRuntime } from '@lindeza/core/hooks';
import { type ReactNode, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toastError } from '../../app/ui';
import { openWhatsapp, useMessageCtx } from '../../app/useMessages';
import { IconChevronRight, IconPlus, IconSearch, IconTriangleAlert, IconUsers, IconWhatsapp } from '../../components/Icons';
import { PhoneInput } from '../../components/PhoneInput';
import { Avatar, Empty, Field, Sheet } from '../../components/ui';
import './clientes.css';

type Tab = 'all' | 'missing' | 'package';

export function Clientes() {
  return <ClientsSplit selectedId={null} />;
}

/** Lista à esquerda e ficha à direita (do tablet em diante). No celular, só um dos dois. */
export function ClientsSplit({ selectedId, children }: { selectedId: string | null; children?: ReactNode }) {
  return (
    <div className={`cl-split${selectedId ? ' cl-split--detail' : ''}`}>
      <ClientList selectedId={selectedId} />
      <div className="cl-side">
        {children ?? (
          <div className="cl-card cl-pick">
            <Empty icon={<IconUsers />}>Toque numa cliente para ver a ficha: observação, histórico e pacote.</Empty>
          </div>
        )}
      </div>
    </div>
  );
}

function useClientRows(): ClientRow[] | undefined {
  const prof = useProfessional();
  const missing = useMissingClients();
  const data = useLiveQuery(
    async (rt) => {
      const [clients, appointments, services, packages] = await Promise.all([
        rt.db.all('client'),
        rt.db.all('appointment'),
        rt.db.all('service'),
        rt.db.all('package'),
      ]);
      return { clients, appointments, services, packages };
    },
    [],
    ['client', 'appointment', 'service', 'package'],
  );
  const tz = prof?.timezone ?? 'America/Sao_Paulo';
  return useMemo(() => (data && missing ? clientRows({ ...data, missing, timezone: tz, now: Date.now() }) : undefined), [data, missing, tz]);
}

function ClientList({ selectedId }: { selectedId: string | null }) {
  const [params, setParams] = useSearchParams();
  const tab: Tab = params.get('aba') === 'sumiu' ? 'missing' : params.get('aba') === 'pacote' ? 'package' : 'all';
  const setTab = (t: Tab) => setParams(t === 'missing' ? { aba: 'sumiu' } : t === 'package' ? { aba: 'pacote' } : {}, { replace: true });
  const rows = useClientRows();
  const missing = useMissingClients();
  const prof = useProfessional();
  const tz = prof?.timezone ?? 'America/Sao_Paulo';
  const today = todayIn(tz);
  const [query, setQuery] = useState('');
  const [adding, setAdding] = useState(false);
  const withPackage = rows?.filter((r) => r.activePackage) ?? [];
  const list = useMemo(() => (rows ?? []).filter((r) => (tab === 'package' ? r.activePackage : true) && matchClient(r.client, query)), [rows, query, tab]);
  const missingCount = missing?.length ?? 0;

  return (
    <section className="cl-col" aria-label="Lista de clientes">
      <header className="cl-head">
        <h1 className="cl-title">Clientes</h1>
        {rows && <span className="cl-count">{rows.length === 1 ? '1 cliente' : `${rows.length} clientes`}</span>}
        <button className="btn btn--soft btn--sm cl-new" onClick={() => setAdding(true)} aria-label="Nova cliente">
          <IconPlus strokeWidth={2.5} /> Nova cliente
        </button>
      </header>

      <label className="cl-search">
        <IconSearch />
        <input placeholder="Nome ou telefone" aria-label="Buscar cliente" value={query} onChange={(e) => setQuery(e.target.value)} />
      </label>

      <div className="segmented cl-seg" role="group" aria-label="Lista">
        <button type="button" aria-pressed={tab === 'all'} onClick={() => setTab('all')}>
          Todas
        </button>
        <button type="button" aria-pressed={tab === 'missing'} onClick={() => setTab('missing')}>
          Quem sumiu{missingCount > 0 ? ` (${missingCount})` : ''}
        </button>
        <button type="button" className="cl-seg-pkg" aria-pressed={tab === 'package'} onClick={() => setTab('package')}>
          Com pacote{withPackage.length > 0 ? ` (${withPackage.length})` : ''}
        </button>
      </div>

      {tab === 'all' && missingCount > 0 && (
        <button type="button" className="cl-band" onClick={() => setTab('missing')}>
          <span className="cl-wa-dot">
            <IconWhatsapp />
          </span>
          <span className="cl-band-txt">
            <b>{missingCount === 1 ? '1 cliente sumiu.' : `${missingCount} clientes sumiram.`}</b>
            <span>Chamar no WhatsApp</span>
          </span>
          <IconChevronRight className="cl-band-go" />
        </button>
      )}

      {tab === 'missing' ? (
        <MissingList />
      ) : rows && rows.length === 0 ? (
        <Empty icon={<IconUsers />}>Suas clientes aparecem aqui quando você marca o primeiro atendimento ou quando elas marcam pelo link.</Empty>
      ) : (
        <ul className="cl-list">
          {list.map((r) => {
            const line = clientLineText(r.line, tz, today);
            return (
              <li key={r.client.id}>
                <Link className="cl-row" to={`/clientes/${r.client.id}`} aria-current={r.client.id === selectedId ? 'page' : undefined}>
                  <Avatar name={r.client.name} />
                  <span className="cl-who">
                    <b className="ellipsis">{r.client.name}</b>
                    <span className="cl-sit">
                      {line.lead && <span className="cl-now">{line.lead}</span>}
                      {line.text && <span className="ellipsis">{line.text}</span>}
                      {line.warn && (
                        <span className="status status--warn">
                          {line.text && <IconTriangleAlert />}
                          {line.warn}
                        </span>
                      )}
                    </span>
                  </span>
                </Link>
              </li>
            );
          })}
          {rows && list.length === 0 && <li className="cl-none muted">{tab === 'package' ? 'Ninguém com pacote ativo.' : 'Ninguém com esse nome.'}</li>}
        </ul>
      )}
      {adding && <NewClientSheet onClose={() => setAdding(false)} />}
    </section>
  );
}

function NewClientSheet({ onClose }: { onClose: () => void }) {
  const rt = useRuntime();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <Sheet title="Nova cliente" sub="O resto vem com os atendimentos." onClose={onClose}>
      <form
        className="stack stack--lg"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          try {
            const c = await rt.actions.createClient({
              name,
              whatsapp: phone || null,
            });
            onClose();
            navigate(`/clientes/${c.id}`);
          } catch (err) {
            toastError(err);
          } finally {
            setBusy(false);
          }
        }}
      >
        <Field label="Nome" htmlFor="new-client-name">
          <input id="new-client-name" className="input" autoFocus value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="WhatsApp" htmlFor="new-client-phone" hint="DDD + número">
          <PhoneInput id="new-client-phone" className="input" value={phone} onChange={setPhone} />
        </Field>
        <button className="btn btn--primary btn--lg btn--block" disabled={busy || !name.trim()}>
          Salvar
        </button>
      </form>
    </Sheet>
  );
}

/** Quem sumiu (5.3): sem visita há mais do que o prazo e sem horário marcado. Um toque chama no WhatsApp. */
function MissingList() {
  const missing = useMissingClients();
  const ctx = useMessageCtx();
  const prof = useProfessional();
  if (!missing || !ctx) return null;
  if (missing.length === 0) {
    return (
      <Empty icon={<IconUsers />}>
        Ninguém sumiu. Aqui aparecem as clientes sem visita há mais de {prof?.missing_after_days ?? 21} dias (ou do ciclo do serviço) e sem horário marcado.
      </Empty>
    );
  }
  return (
    <div className="cl-missing">
      <p className="cl-help">Sem visita há mais de {prof?.missing_after_days ?? 21} dias e sem horário marcado. Um toque para chamar de volta.</p>
      <ul className="cl-list">
        {missing.map((m) => (
          <li key={m.client.id} className="cl-gone">
            <Link className="cl-row" to={`/clientes/${m.client.id}`}>
              <Avatar name={m.client.name} />
              <span className="cl-who">
                <b className="ellipsis">{m.client.name}</b>
                <span className="cl-sit">
                  {m.lastService && <span className="ellipsis">{m.lastService.name}</span>}
                  <span className="status status--warn">{m.daysSince} dias sem vir</span>
                </span>
              </span>
            </Link>
            <button
              className="btn btn--whatsapp btn--sm"
              aria-label={`Chamar ${m.client.name} no WhatsApp`}
              onClick={() => openWhatsapp(m.client.whatsapp, missingMessage(ctx, m.client, m.lastService, m.weeksSince))}
            >
              <IconWhatsapp /> Chamar
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
